/**
 * Kalkulationslogik – Klappbox (Magnet-Umschlagbox)
 *
 * WICHTIG: Diese Datei ist die EINZIGE Quelle der Preislogik für diesen Boxtyp.
 * Sie wird unverändert sowohl im Theme (Browser, für die Live-Vorschau) als auch
 * im Backend (PHP, für die verbindliche Preisberechnung beim Bestellen)
 * eingesetzt. Nach einer Änderung an dieser Datei IMMER auch
 * backend-php/private/lib/calculators/klappbox.php von Hand nachziehen (siehe
 * Kommentar dort) – das Sync-Skript kopiert nur die JS-Variante.
 *
 * Aufbau (nach Kundenvorgabe, Stand 30.07.2026):
 *   Ausrichtung: Quer ODER Hochkant (bestimmt, welche Innenmaß-Angabe in den
 *     Formeln als "lang" bzw. "kurz" gilt)
 *   -> Innenmaß Unterteil: Länge × Breite × Höhe
 *   -> Material Karton (innen): Schwarz ODER Weiß (bildet den eigentlichen
 *      Boxkorpus)
 *   -> Umschlag (Magnetklappe): IMMER 1,5mm Graupappe, 4 Teile (Front, Top,
 *      Rücken, Boden) – kein Kundenwahlfeld, da Material fest vorgegeben
 *   -> Bezugspapier: Naturpapier (Wibalin) ODER 4C gedruckt – schließen sich
 *      aus, identische Logik/Preise wie bei der Stülpdeckelbox. Kleidet drei
 *      Zuschnitte ein: Box, Umschlag außen, Umschlag innen
 *   -> Kartenhalter (optionaler Aufpreis): Keine / Kartenschlitze /
 *      4x Dreieckstaschen / Tasche aus Karton
 *   -> Veredelung Ja/Nein (= Logoprägung, inkl. Klischeegröße) – identische
 *      Logik/Preise wie bei der Stülpdeckelbox ("Rest wie bei
 *      Stülpdeckelkarton")
 *
 * Nutzenberechnung (wie viele Zuschnitte passen auf einen Bogen): identische
 * "Überkreuz"-Logik wie bei der Stülpdeckelbox (floorFit: normale UND um 90°
 * gedrehte Anordnung, jeweils Maximum). Gegen das Kundenbeispiel geprüft:
 * Box 200×100×50mm -> offenes Maß 300×200mm -> auf Bogen 1100×700mm (Schwarz)
 * ergibt das genau 10 Nutzen/Bogen (Kundenvorgabe), siehe Node-Test.
 *
 * ACHTUNG – offene Punkte, mit Tobi/Kunde noch zu klären (siehe auch
 * Rückmeldung im Chat):
 *   1. "Ausrichtung" (quer/hochkant) wird als Pflichtfeld abgefragt und
 *      bestimmt direkt, welches Maß (Länge oder Breite) in den Formeln als
 *      "lang" gilt. Es gibt KEINEN automatischen Abgleich mit den tatsächlich
 *      eingegebenen Maßen – bei einem Widerspruch (z. B. "quer" gewählt, aber
 *      Breite > Länge) erscheint nur ein weicher Hinweis, es wird nichts
 *      blockiert.
 *   2. Bei der Papierberechnung für "Umschlag außen"/"Umschlag innen" nutzt
 *      diese Datei für "Top breite"/"Boden breite" den ROHEN kurzen Wert
 *      (ohne die +3mm, die beim Pappzuschnitt des Umschlags dazukommen) – so
 *      wörtlich wie möglich nach Kundentext umgesetzt, aber nicht 100%
 *      eindeutig aus der Vorlage ablesbar.
 *   3. Mindesthöhe: Anders als bei der Stülpdeckelbox (Deckelhöhe mind. 15mm)
 *      wurde für die Klappbox keine Mindesthöhe vorgegeben – es gilt nur die
 *      allgemeine Mindestgröße 30mm für Länge/Breite.
 *
 * Vertrag:
 *   calculate(inputs) -> Ergebnisobjekt mit { valid, note, ...Detailfeldern }
 *   inputs = {
 *     orientation: 'quer' | 'hochkant',           // Pflicht
 *     innerLength: number (mm, mind. 30),
 *     innerWidth:  number (mm, mind. 30),
 *     innerHeight: number (mm),
 *     qty:         number (Stück, ganzzahlig, >= 1),
 *
 *     materialInnen: 'schwarz' | 'weiss',          // immer Pflicht
 *
 *     bezugspapierAuswahl:   'naturpapier' | 'druck4c'    // immer Pflicht
 *     bezugspapierFarbe:     string, z. B. 'WBN500'       // nur bei 'naturpapier'
 *     bezugspapierFarbeName: string                       // nur bei 'naturpapier'
 *
 *     kartenhalter: 'keine' | 'schlitze' | 'dreieckstaschen' | 'tasche',
 *
 *     veredelung:  boolean,                        // Ja/Nein = Logoprägung
 *     clicheSize:  'A7' | 'A6' | 'A5' | 'A4' | ''   // nur wenn veredelung === true
 *   }
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.BoxCalculators = root.BoxCalculators || {};
    root.BoxCalculators.klappbox = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Mindestmaß für Innenmaß Länge/Breite (wie Stülpdeckelbox).
  var MIN_INNER_SIZE = 30;

  // -- Karton innen (struktureller Korpus der Box) --------------------------
  var KARTON_INNEN_OPTIONS = {
    schwarz: { name: 'Schwarz', sheetLength: 1100, sheetWidth: 700, sheetPrice: 3.30 },
    weiss: { name: 'Weiß', sheetLength: 1050, sheetWidth: 800, sheetPrice: 2.60 }
  };
  var DEFAULT_MATERIAL_INNEN = 'schwarz';

  // -- Umschlag (Magnetklappe): IMMER 1,5mm Graupappe, kein Kundenwahlfeld --
  var UMSCHLAG_GRAUPAPPE = { name: 'Graupappe 1,5mm', sheetLength: 1050, sheetWidth: 800, sheetPrice: 2.60 };

  // -- Bezugsmaterial, Option 1: Naturpapier (Wibalin Natural) -- identisch
  //    zur Stülpdeckelbox.
  var COVER = { name: 'Naturpapier Wibalin', sheetLength: 1020, sheetWidth: 760, sheetPrice: 2.15 };

  // Fallback, falls trotz Pflichtfeld keine Farbe übermittelt wird.
  var DEFAULT_COVER_COLOR_SKU = 'WBN500';
  var DEFAULT_COVER_COLOR_NAME = 'White';

  // -- Bezugsmaterial, Option 2: 4C gedruckt -- identisch zur Stülpdeckelbox.
  var DRUCK4C_SHEET = { sheetLength: 1000, sheetWidth: 700 };
  var DRUCK4C_PRICE_TIERS = [
    { maxSheets: 10, price: 7.00 },
    { maxSheets: 50, price: 3.00 },
    { maxSheets: 100, price: 2.00 },
    { maxSheets: 200, price: 1.80 }
  ];
  // Für Bestellungen über 200 Bögen liegt noch kein Preis vor – vorläufig
  // wird der günstigste bekannte Preis (1,80 €) weiterverwendet (wie bei der
  // Stülpdeckelbox).
  var DRUCK4C_PRICE_ABOVE_MAX_TIER = 1.80;

  function getDruck4cPricePerSheet(totalSheets) {
    for (var i = 0; i < DRUCK4C_PRICE_TIERS.length; i++) {
      if (totalSheets <= DRUCK4C_PRICE_TIERS[i].maxSheets) {
        return DRUCK4C_PRICE_TIERS[i].price;
      }
    }
    return DRUCK4C_PRICE_ABOVE_MAX_TIER;
  }

  var SETUP_FEE = 100;

  // -- Veredelung: Logoprägung -- identisch zur Stülpdeckelbox.
  var EMBOSSING_BASE = 0.30;
  var EMBOSSING_SETUP = 30;
  var CLICHE_PRICES = {
    A7: 60,
    A6: 80,
    A5: 110,
    A4: 160
  };

  // -- Kartenhalter (optionaler Aufpreis pro Stück) --------------------------
  var KARTENHALTER_OPTIONS = {
    keine: { name: 'Keine', price: 0 },
    schlitze: { name: 'Kartenschlitze', price: 1.50 },
    dreieckstaschen: { name: '4x Dreieckstaschen (32×32mm)', price: 0.80 },
    tasche: { name: 'Tasche aus Karton', price: 2.30 }
  };
  var DEFAULT_KARTENHALTER = 'keine';

  // Deutsche Mehrwertsteuer, nur für die Anzeige (netto/brutto) relevant –
  // alle übrigen Preise in dieser Datei sind Netto-Preise.
  var VAT_RATE = 0.19;

  function num(value) {
    return Math.max(parseFloat(value || 0), 0);
  }

  // Rundet auf volle Cent (kaufmännisch).
  function round2(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  function floorFit(sheetLength, sheetWidth, itemLength, itemWidth) {
    if (itemLength <= 0 || itemWidth <= 0) return 0;
    var normal = Math.floor(sheetLength / itemLength) * Math.floor(sheetWidth / itemWidth);
    var rotated = Math.floor(sheetLength / itemWidth) * Math.floor(sheetWidth / itemLength);
    return Math.max(normal, rotated);
  }

  // Größenkategorien für Zuschnitt/Heften/Beziehen/Einpacken – eigene
  // Preispunkte für die Klappbox (aufwendiger als die Stülpdeckelbox wegen
  // des zusätzlichen Umschlags).
  function getProductionTier(maxSide) {
    if (maxSide <= 80) return { name: 'Sehr klein', price: 9.00 };
    if (maxSide <= 150) return { name: 'Klein', price: 9.90 };
    if (maxSide <= 250) return { name: 'Mittel', price: 13.80 };
    return { name: 'Groß', price: 18.00 };
  }

  function calculate(inputs) {
    inputs = inputs || {};

    var orientation = inputs.orientation === 'hochkant' ? 'hochkant' : 'quer';

    var innerLength = num(inputs.innerLength);
    var innerWidth = num(inputs.innerWidth);
    var innerHeight = num(inputs.innerHeight);
    var qty = Math.max(parseInt(inputs.qty || 1, 10) || 1, 1);

    var materialInnenKey = KARTON_INNEN_OPTIONS.hasOwnProperty(inputs.materialInnen) ? inputs.materialInnen : DEFAULT_MATERIAL_INNEN;
    var karton = KARTON_INNEN_OPTIONS[materialInnenKey];

    var bezugspapierAuswahl = inputs.bezugspapierAuswahl === 'druck4c' ? 'druck4c' : 'naturpapier';
    var bezugspapierFarbeSku = (inputs.bezugspapierFarbe || '').trim();
    var bezugspapierFarbeName = (inputs.bezugspapierFarbeName || '').trim();

    var kartenhalterKey = KARTENHALTER_OPTIONS.hasOwnProperty(inputs.kartenhalter) ? inputs.kartenhalter : DEFAULT_KARTENHALTER;
    var kartenhalter = KARTENHALTER_OPTIONS[kartenhalterKey];

    var veredelung = Boolean(inputs.veredelung);
    var clicheSize = inputs.clicheSize || '';

    var result = {
      valid: false,
      note: 'Bitte geben Sie Ihre Maße ein, um eine Orientierungskalkulation zu erhalten.'
    };

    if (!innerLength || !innerWidth || !innerHeight) {
      return result;
    }

    if (innerLength < MIN_INNER_SIZE || innerWidth < MIN_INNER_SIZE) {
      result.note = 'Innenmaß Länge und Breite müssen jeweils mindestens ' + MIN_INNER_SIZE + ' mm betragen.';
      return result;
    }

    if (veredelung && (!clicheSize || !CLICHE_PRICES.hasOwnProperty(clicheSize))) {
      result.note = 'Bitte wählen Sie eine Klischeegröße aus.';
      return result;
    }

    var embossingFormatNotice = (veredelung && (innerLength > 280 || innerWidth > 190))
      ? ' Hinweis: Die maximale Prägefläche beträgt 280 × 190 mm. Bitte prüfen Sie, ob das Motiv innerhalb dieser Fläche liegt.'
      : '';

    // Weicher Hinweis (blockiert nicht), falls die gewählte Ausrichtung nicht
    // zu den eingegebenen Maßen passt.
    var orientationNotice = '';
    if (orientation === 'quer' && innerWidth > innerLength) {
      orientationNotice = ' Hinweis: Sie haben "Quer" gewählt, Ihre Breite ist jedoch größer als die Länge – bitte prüfen Sie Ihre Angaben.';
    } else if (orientation === 'hochkant' && innerLength > innerWidth) {
      orientationNotice = ' Hinweis: Sie haben "Hochkant" gewählt, Ihre Länge ist jedoch größer als die Breite – bitte prüfen Sie Ihre Angaben.';
    }

    // -- Karton innen (Boxkorpus): offenes Maß + Nutzen pro Bogen ------------
    var openLength = innerLength + (2 * innerHeight);
    var openWidth = innerWidth + (2 * innerHeight);

    var kartonInnenUnits = floorFit(karton.sheetLength, karton.sheetWidth, openLength, openWidth);
    if (!kartonInnenUnits) {
      result.note = 'Das offene Format ist in dieser Größe aktuell nicht kalkulierbar. Bitte prüfen Sie Ihre Maße.';
      return result;
    }
    var cartonCostPerPiece = karton.sheetPrice / kartonInnenUnits;

    // -- Umschlag-Graupappe: 4 Teile (Front/Rücken identisch, Top/Boden
    //    identisch), lang/kurz je nach Ausrichtung. --
    var long = orientation === 'quer' ? innerLength : innerWidth;
    var short = orientation === 'quer' ? innerWidth : innerLength;

    var frontPanel = { length: long + 10, width: innerHeight };
    var topPanel = { length: long + 10, width: short + 3 };

    var frontRueckenUnits = floorFit(UMSCHLAG_GRAUPAPPE.sheetLength, UMSCHLAG_GRAUPAPPE.sheetWidth, frontPanel.length, frontPanel.width);
    var topBodenUnits = floorFit(UMSCHLAG_GRAUPAPPE.sheetLength, UMSCHLAG_GRAUPAPPE.sheetWidth, topPanel.length, topPanel.width);

    if (!frontRueckenUnits || !topBodenUnits) {
      result.note = 'Das offene Format des Umschlags ist in dieser Größe aktuell nicht kalkulierbar. Bitte prüfen Sie Ihre Maße.';
      return result;
    }

    var umschlagPappeCostPerPiece =
      2 * (UMSCHLAG_GRAUPAPPE.sheetPrice / frontRueckenUnits) +
      2 * (UMSCHLAG_GRAUPAPPE.sheetPrice / topBodenUnits);

    // -- Bezugspapier: Naturpapier ODER 4C gedruckt (schließen sich aus) --
    //    Drei Zuschnitte: Box, Umschlag außen, Umschlag innen.
    var boxWrap = { length: (2 * short) + long + 30, width: innerHeight + 30 };
    var umschlagAussenWrap = { length: (2 * innerHeight) + (2 * short) + 42, width: long + 30 };
    var umschlagInnenWrap = { length: innerHeight + short + 30, width: long - 5 };

    var coverBoxUnits = null;
    var coverAussenUnits = null;
    var coverInnenUnits = null;
    var coverCostPerPiece = 0;
    var coverColorName = '';
    var druck4cTotalSheets = null;
    var druck4cPricePerSheet = null;

    if (bezugspapierAuswahl === 'druck4c') {
      var druck4cBoxUnits = floorFit(DRUCK4C_SHEET.sheetLength, DRUCK4C_SHEET.sheetWidth, boxWrap.length, boxWrap.width);
      var druck4cAussenUnits = floorFit(DRUCK4C_SHEET.sheetLength, DRUCK4C_SHEET.sheetWidth, umschlagAussenWrap.length, umschlagAussenWrap.width);
      var druck4cInnenUnits = floorFit(DRUCK4C_SHEET.sheetLength, DRUCK4C_SHEET.sheetWidth, umschlagInnenWrap.length, umschlagInnenWrap.width);

      if (!druck4cBoxUnits || !druck4cAussenUnits || !druck4cInnenUnits) {
        result.note = 'Das offene Format ist für den Druckbogen (70 × 100 cm) aktuell nicht kalkulierbar. Bitte prüfen Sie Ihre Maße.';
        return result;
      }

      druck4cTotalSheets =
        Math.ceil(qty / druck4cBoxUnits) +
        Math.ceil(qty / druck4cAussenUnits) +
        Math.ceil(qty / druck4cInnenUnits);
      druck4cPricePerSheet = getDruck4cPricePerSheet(druck4cTotalSheets);

      coverCostPerPiece = (druck4cTotalSheets * druck4cPricePerSheet) / qty;
      coverColorName = '4C gedruckt (Druckdaten folgen im weiteren Projektverlauf)';
      bezugspapierFarbeSku = '';
    } else {
      coverBoxUnits = floorFit(COVER.sheetLength, COVER.sheetWidth, boxWrap.length, boxWrap.width);
      coverAussenUnits = floorFit(COVER.sheetLength, COVER.sheetWidth, umschlagAussenWrap.length, umschlagAussenWrap.width);
      coverInnenUnits = floorFit(COVER.sheetLength, COVER.sheetWidth, umschlagInnenWrap.length, umschlagInnenWrap.width);

      if (!coverBoxUnits || !coverAussenUnits || !coverInnenUnits) {
        result.note = 'Das offene Format ist in dieser Größe aktuell nicht kalkulierbar. Bitte prüfen Sie Ihre Maße.';
        return result;
      }

      coverCostPerPiece =
        (COVER.sheetPrice / coverBoxUnits) +
        (COVER.sheetPrice / coverAussenUnits) +
        (COVER.sheetPrice / coverInnenUnits);

      coverColorName = bezugspapierFarbeName || bezugspapierFarbeSku || DEFAULT_COVER_COLOR_NAME;
      if (!bezugspapierFarbeSku) {
        bezugspapierFarbeSku = DEFAULT_COVER_COLOR_SKU;
      }
    }

    var productionTier = getProductionTier(Math.max(innerLength, innerWidth, innerHeight));
    var setupPerPiece = SETUP_FEE / qty;

    var veredelungPerPiece = 0;
    var veredelungNote = 'Ohne Veredelung';

    if (veredelung) {
      var clicheCost = CLICHE_PRICES[clicheSize] || 0;
      veredelungPerPiece = EMBOSSING_BASE + (EMBOSSING_SETUP / qty) + (clicheCost / qty);
      veredelungNote = 'Logoprägung inkl. Klischee ' + clicheSize;
    }

    var totalPerPiece =
      cartonCostPerPiece +
      umschlagPappeCostPerPiece +
      coverCostPerPiece +
      productionTier.price +
      kartenhalter.price +
      setupPerPiece +
      veredelungPerPiece;

    var total = totalPerPiece * qty;

    // WICHTIG (Rundungskonsistenz Checkout, wie bei der Stülpdeckelbox):
    // "total" ist der ungerundete Rohwert (nur zur Anzeige). "totalRounded"
    // ist der EINMALIG gerundete Gesamtpreis und genau der Betrag, der im
    // Backend als Draft-Order-Zeilenpreis an Shopify übergeben wird
    // (quantity 1, price totalRounded – siehe draft-order.php).
    var totalRounded = round2(total);

    // Netto ist die Kalkulationsgrundlage; für die Anzeige zusätzlich MwSt./Brutto.
    var totalPerPieceVat = totalPerPiece * VAT_RATE;
    var totalPerPieceGross = totalPerPiece + totalPerPieceVat;
    var totalVat = total * VAT_RATE;
    var totalGross = total + totalVat;

    return {
      valid: true,
      note: 'Unser Produktionsteam wählt je nach Anforderungen eigenständig die Dicke des Kartons aus.' + embossingFormatNotice + orientationNotice,

      orientation: orientation,
      innerLength: innerLength,
      innerWidth: innerWidth,
      innerHeight: innerHeight,
      qty: qty,

      materialInnen: materialInnenKey,
      materialName: karton.name,

      bezugspapierAuswahl: bezugspapierAuswahl,
      bezugspapierFarbeSku: bezugspapierFarbeSku,
      bezugspapierFarbeName: coverColorName,

      kartenhalter: kartenhalterKey,
      kartenhalterName: kartenhalter.name,
      kartenhalterPrice: kartenhalter.price,

      veredelung: veredelung,
      veredelungNote: veredelungNote,
      clicheSize: veredelung ? clicheSize : '',
      druck4cTotalSheets: druck4cTotalSheets,
      druck4cPricePerSheet: druck4cPricePerSheet,

      openLength: openLength,
      openWidth: openWidth,

      kartonInnenUnits: kartonInnenUnits,
      frontRueckenUnits: frontRueckenUnits,
      topBodenUnits: topBodenUnits,
      coverBoxUnits: coverBoxUnits,
      coverAussenUnits: coverAussenUnits,
      coverInnenUnits: coverInnenUnits,

      cartonCostPerPiece: cartonCostPerPiece,
      umschlagPappeCostPerPiece: umschlagPappeCostPerPiece,
      coverCostPerPiece: coverCostPerPiece,
      productionTier: productionTier,
      setupPerPiece: setupPerPiece,
      veredelungPerPiece: veredelungPerPiece,

      totalPerPiece: totalPerPiece,
      total: total,
      totalRounded: totalRounded,

      vatRate: VAT_RATE,
      totalPerPieceVat: totalPerPieceVat,
      totalPerPieceGross: totalPerPieceGross,
      totalVat: totalVat,
      totalGross: totalGross
    };
  }

  /**
   * Liefert die Konfiguration als flache Liste von Label/Wert-Paaren, wie sie
   * als Line-Item-Properties auf der Shopify-Bestellung erscheinen sollen.
   *
   * Sichtbarkeit: Property-Namen, die mit einem Unterstrich ("_") beginnen,
   * werden von Shopify automatisch als "hidden properties" behandelt – sie
   * erscheinen NICHT im Warenkorb, Checkout, in der Bestellbestätigungs-Mail
   * oder im Kundenkonto, sind aber ganz normal auf der Bestellung im Shopify
   * Admin sichtbar (siehe auch stuelpdeckelbox.js).
   */
  function describe(calc, extra) {
    if (!calc || !calc.valid) return {};

    var props = {
      'Ausrichtung': calc.orientation === 'hochkant' ? 'Hochkant' : 'Quer',
      'Stückzahl': calc.qty + ' Stück',
      'Innenmaß (L × B × H)': calc.innerLength + ' × ' + calc.innerWidth + ' × ' + calc.innerHeight + ' mm',
      'Material Karton (innen)': calc.materialName,
      'Bezugspapier': calc.bezugspapierAuswahl === 'druck4c'
        ? '4C gedruckt'
        : ('Naturpapier – ' + calc.bezugspapierFarbeName + (calc.bezugspapierFarbeSku ? ' (' + calc.bezugspapierFarbeSku + ')' : '')),
      'Veredelung': calc.veredelungNote
    };

    if (calc.clicheSize) {
      props['Klischeegröße'] = calc.clicheSize;
    }

    if (calc.kartenhalter !== 'keine') {
      props['Kartenhalter'] = calc.kartenhalterName;
    }

    // -- Ab hier: rein interne/technische Angaben, nur für Admin/Produktion
    //    (Property-Name beginnt bewusst mit "_"). --
    if (calc.druck4cTotalSheets) {
      props['_Druckbögen (70×100 cm)'] = calc.druck4cTotalSheets + ' Stück à ' + calc.druck4cPricePerSheet.toFixed(2).replace('.', ',') + ' €';
    }

    props['_Offenes Maß Karton innen'] = calc.openLength + ' × ' + calc.openWidth + ' mm';
    props['_Nutzen Karton innen'] = calc.kartonInnenUnits;
    props['_Nutzen Umschlag-Graupappe (Front/Rücken – Top/Boden)'] = calc.frontRueckenUnits + ' / ' + calc.topBodenUnits;

    if (calc.bezugspapierAuswahl === 'naturpapier') {
      props['_Nutzen Naturpapier (Box – Umschlag außen – Umschlag innen)'] =
        calc.coverBoxUnits + ' / ' + calc.coverAussenUnits + ' / ' + calc.coverInnenUnits;
    }

    props['_Fertigungskategorie'] = calc.productionTier.name;
    props['_Preis pro Stück (netto, rechnerisch)'] = calc.totalPerPiece.toFixed(2).replace('.', ',') + ' €';
    props['_MwSt. (' + Math.round(calc.vatRate * 100) + ' %) pro Stück'] = calc.totalPerPieceVat.toFixed(2).replace('.', ',') + ' €';
    props['_Preis pro Stück (brutto)'] = calc.totalPerPieceGross.toFixed(2).replace('.', ',') + ' €';

    // Der verbindliche Gesamtpreis bleibt für den Kunden sichtbar (kein "_").
    props['Gesamtpreis (netto, verbindlich)'] = calc.totalRounded.toFixed(2).replace('.', ',') + ' €';

    if (extra && extra.notes) {
      props['Sonderwünsche'] = extra.notes;
    }

    return props;
  }

  return {
    calculate: calculate,
    describe: describe,
    materials: {
      KARTON_INNEN_OPTIONS: KARTON_INNEN_OPTIONS,
      COVER: COVER,
      UMSCHLAG_GRAUPAPPE: UMSCHLAG_GRAUPAPPE,
      KARTENHALTER_OPTIONS: KARTENHALTER_OPTIONS
    },
    CLICHE_PRICES: CLICHE_PRICES,
    VAT_RATE: VAT_RATE
  };
});
